/* Main product (Blanca PDP) behaviours: size guide drawer (gender / unit toggles), animated
   accordions, variant-driven state swaps (badge, notify-me, installments, sticky bar) and the
   bottom sticky add-to-cart bar. Bound once per <product-info> by <pdp-behaviors>; idempotent on
   theme-editor section reloads (everything is torn down in disconnectedCallback). */
if (!customElements.get('pdp-behaviors')) {
  const CM_PER_INCH = 2.54;

  const reducedMotion = () =>
    (window.Theme && Theme.config && Theme.config.motionReduced) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const animate = (el, keyframes, duration = 0.3) => {
    if (reducedMotion() || !window.Theme || !Theme.motion) return Promise.resolve();
    return Theme.motion
      .animate(el, keyframes, { duration, easing: [0.39, 0.575, 0.565, 1.0] })
      .finished.catch(() => {});
  };

  // "104–110" (cm) -> "41–43.5" (inches, nearest half inch)
  const toInches = (value) =>
    value.replace(/\d+(?:\.\d+)?/g, (n) => {
      const inch = Math.round((parseFloat(n) / CM_PER_INCH) * 2) / 2;
      return Number.isInteger(inch) ? String(inch) : inch.toFixed(1);
    });

  class PdpBehaviors extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.sectionId = this.dataset.sectionId;
      this.onClick = this.onClick.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onTableScroll = this.onTableScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.root.addEventListener('click', this.onClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.bindDrawer();
      this.bindSticky();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
      }
    }

    disconnectedCallback() {
      if (!this.root) return;
      this.root.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.drawer) {
        this.drawer.removeEventListener('cancel', this.onCancel);
        if (this.drawer.open) this.drawer.close();
      }
      if (this.tableWrap) this.tableWrap.removeEventListener('scroll', this.onTableScroll);
      document.body.classList.remove('overflow-hidden');
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      if (this.unsubscribe) this.unsubscribe();
    }

    /* ---------- clicks (delegated: variant-selects is re-rendered on every change) ---------- */

    onClick(event) {
      const target = event.target;

      const opener = target.closest('[data-size-guide-open]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener);
        return;
      }

      if (this.drawer && this.drawer.contains(target)) {
        if (target === this.drawer || target.closest('[data-size-guide-close]')) {
          this.closeDrawer();
          return;
        }
        const chartBtn = target.closest('[data-size-guide-chart]');
        if (chartBtn) return this.setChart(chartBtn.dataset.sizeGuideChart);
        const unitBtn = target.closest('[data-size-guide-unit]');
        if (unitBtn) return this.setUnit(unitBtn.dataset.sizeGuideUnit);
        return;
      }

      const summary = target.closest('[data-pdp-accordion] > summary');
      if (summary) {
        event.preventDefault();
        this.toggleAccordion(summary.parentElement);
        return;
      }

      const notifyToggle = target.closest('[data-pdp-notify-toggle]');
      if (notifyToggle) {
        this.toggleNotify(notifyToggle);
        return;
      }

      const stickyCta = target.closest('[data-pdp-sticky-cta]');
      if (stickyCta) this.onStickyCta();
    }

    /* ---------- accordions ---------- */

    async toggleAccordion(details) {
      if (details.dataset.animating) return;
      const content = details.querySelector('.pdp-accordion__content');
      details.classList.toggle('is-open', !details.open);
      if (!content || reducedMotion() || !window.Theme || !Theme.motion) {
        details.open = !details.open;
        return;
      }
      details.dataset.animating = 'true';
      if (details.open) {
        await animate(content, { height: [`${content.offsetHeight}px`, '0px'], opacity: [1, 0] });
        details.open = false;
      } else {
        details.open = true;
        await animate(content, { height: ['0px', `${content.offsetHeight}px`], opacity: [0, 1] });
      }
      for (const animation of content.getAnimations()) animation.cancel();
      delete details.dataset.animating;
    }

    /* ---------- size guide drawer ---------- */

    bindDrawer() {
      this.drawer = this.root.querySelector('[data-size-guide]');
      if (!this.drawer) return;
      this.drawer.addEventListener('cancel', this.onCancel);
      this.chart = 'girls';
      this.unit = 'cm';
      this.tableWrap = this.drawer.querySelector('[data-size-guide-scroll]');
      this.thumb = this.drawer.querySelector('[data-size-guide-thumb]');
      if (this.tableWrap) this.tableWrap.addEventListener('scroll', this.onTableScroll, { passive: true });
    }

    get isDesktop() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    async openDrawer(trigger) {
      if (!this.drawer || this.drawer.open) return;
      this.trigger = trigger || null;
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      this.drawer.showModal();
      document.body.classList.add('overflow-hidden');
      // focus the panel (not the close button) so no ring paints on pointer open; Tab reaches the controls
      const panelEl = this.drawer.querySelector('[data-size-guide-panel]');
      if (panelEl) panelEl.focus({ preventScroll: true });
      this.onTableScroll();
      const panel = this.drawer.querySelector('[data-size-guide-panel]');
      const offscreen = this.isDesktop ? 'translateX(100%)' : 'translateY(100%)';
      await animate(panel, { transform: [offscreen, 'translate(0, 0)'] }, 0.35);
    }

    async closeDrawer() {
      if (!this.drawer || !this.drawer.open || this.closing) return;
      this.closing = true;
      const panel = this.drawer.querySelector('[data-size-guide-panel]');
      const offscreen = this.isDesktop ? 'translateX(100%)' : 'translateY(100%)';
      await animate(panel, { transform: ['translate(0, 0)', offscreen] }, 0.3);
      this.drawer.close();
      for (const animation of panel.getAnimations()) animation.cancel();
      this.closing = false;
      document.body.classList.remove('overflow-hidden');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        // variant-selects may have been re-rendered: fall back to the live trigger
        const live = this.trigger.isConnected ? this.trigger : this.root.querySelector('[data-size-guide-open]');
        if (live) live.focus({ preventScroll: true });
      }
      this.trigger = null;
    }

    onCancel(event) {
      event.preventDefault();
      this.closeDrawer();
    }

    onBlockSelect(event) {
      // the size_guide block's editor attributes live on the dialog itself
      if (this.drawer && event.target === this.drawer) this.openDrawer(null);
    }

    onBlockDeselect(event) {
      if (this.drawer && event.target === this.drawer) this.closeDrawer();
    }

    setChart(name) {
      this.chart = name;
      this.drawer.querySelectorAll('[data-size-guide-chart]').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.sizeGuideChart === name));
      });
      this.drawer.querySelectorAll('[data-size-guide-table], [data-size-guide-cards]').forEach((el) => {
        el.hidden = (el.dataset.sizeGuideTable ?? el.dataset.sizeGuideCards) !== name;
      });
      this.onTableScroll();
    }

    setUnit(unit) {
      this.unit = unit;
      this.drawer.querySelectorAll('[data-size-guide-unit]').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.sizeGuideUnit === unit));
      });
      this.drawer.querySelectorAll('td[data-cm]').forEach((cell) => {
        cell.textContent = unit === 'in' ? toInches(cell.dataset.cm) : cell.dataset.cm;
      });
      this.drawer.querySelectorAll('caption').forEach((caption) => {
        caption.textContent = caption.textContent.replace(/\((.*)\)$/, unit === 'in' ? '(in)' : '(cm)');
      });
      const note = this.drawer.querySelector('[data-size-guide-note]');
      if (note) note.textContent = unit === 'in' ? note.dataset.noteIn : note.dataset.noteCm;
    }

    onTableScroll() {
      if (!this.tableWrap || !this.thumb) return;
      const { scrollWidth, clientWidth, scrollLeft } = this.tableWrap;
      const track = this.thumb.parentElement;
      if (scrollWidth <= clientWidth + 1) {
        track.hidden = true;
        return;
      }
      track.hidden = false;
      const ratio = clientWidth / scrollWidth;
      const trackWidth = track.clientWidth;
      const thumbWidth = Math.max(24, trackWidth * ratio);
      const maxX = trackWidth - thumbWidth;
      const x = maxX * (scrollLeft / (scrollWidth - clientWidth));
      this.thumb.style.setProperty('--sg-thumb-width', `${thumbWidth}px`);
      this.thumb.style.setProperty('--sg-thumb-x', `${x}px`);
    }

    /* ---------- notify me ---------- */

    toggleNotify(button) {
      const fields = this.root.querySelector('[data-pdp-notify-fields]');
      if (!fields) return;
      const open = fields.hidden;
      fields.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
      if (open) {
        const input = fields.querySelector('input[type="email"]');
        if (input) input.focus();
      }
    }

    /* ---------- variant change: swap state-dependent regions from the fetched section ---------- */

    onVariantChange(event) {
      const data = event && event.data;
      if (!data || !data.html || data.sectionId !== (this.root.dataset.originalSection || this.root.dataset.section)) return;
      const html = data.html;
      this.root.querySelectorAll('[data-pdp-swap]').forEach((el) => {
        if (!el.id) return;
        const source = html.getElementById(el.id);
        if (!source) return;
        if (el.dataset.pdpSwap === 'html') el.innerHTML = source.innerHTML;
        el.className = source.className;
        el.hidden = source.hidden;
        if (source.dataset.state) el.dataset.state = source.dataset.state;
      });
      const body = this.root.querySelector('[data-pdp-notify-body]');
      const newBody = html.querySelector('[data-pdp-notify-body]');
      if (body && newBody) body.value = newBody.value;
      const fields = this.root.querySelector('[data-pdp-notify-fields]');
      const toggle = this.root.querySelector('[data-pdp-notify-toggle]');
      if (fields && !this.root.querySelector('.pdp-buy--sold-out')) {
        fields.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
      this.syncSticky();
    }

    /* ---------- sticky add-to-cart bar ---------- */

    bindSticky() {
      this.sticky = this.root.querySelector('[data-pdp-sticky]');
      this.buy = this.root.querySelector('[data-pdp-buy]');
      if (!this.sticky || !this.buy || !('IntersectionObserver' in window)) return;

      this.sticky.classList.remove('is-ready');
      this.buyOutOfView = this.buy.getBoundingClientRect().bottom <= 0;
      this.nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 40;
      this.syncSticky();
      requestAnimationFrame(() => this.sticky?.classList.add('is-ready'));

      this.observer = new IntersectionObserver(
        ([entry]) => {
          // only once the primary button has scrolled up past the top of the viewport
          this.buyOutOfView = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.syncSticky();
        },
        { threshold: 0 }
      );
      this.observer.observe(this.buy);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    onScroll() {
      if (!this.sticky) return;
      // hide again when the footer region is reached so the bar never covers it
      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 40;
      if (nearBottom !== this.nearBottom) {
        this.nearBottom = nearBottom;
        this.syncSticky();
      }
    }

    syncSticky() {
      if (!this.sticky) return;
      const drawerOpen = this.drawer && this.drawer.open;
      const show = this.buyOutOfView && !this.nearBottom && !drawerOpen;
      this.sticky.classList.toggle('is-visible', Boolean(show));
      if (show) {
        this.sticky.removeAttribute('inert');
        this.sticky.setAttribute('aria-hidden', 'false');
      } else {
        this.sticky.setAttribute('inert', '');
        this.sticky.setAttribute('aria-hidden', 'true');
      }
    }

    onStickyCta() {
      const state = this.sticky ? this.sticky.dataset.state : 'add';
      if (state === 'add') {
        const form = this.root.querySelector(`#product-form-${this.sectionId}`);
        const submit = this.root.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (form && submit && !submit.disabled) {
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submit);
          else submit.click();
        }
        return;
      }
      const focusTarget =
        state === 'notify'
          ? this.root.querySelector('[data-pdp-notify-toggle]')
          : this.root.querySelector('variant-selects input:not(:checked), variant-selects select');
      const scrollTarget = state === 'notify' ? this.buy : this.root.querySelector('variant-selects');
      if (scrollTarget) scrollTarget.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      if (state === 'notify' && focusTarget) this.toggleNotify(focusTarget);
    }
  }

  customElements.define('pdp-behaviors', PdpBehaviors);
}
